import { createContext, useContext, useState, ReactNode } from 'react'
import { useHits, useInstantSearch } from 'react-instantsearch'
import { Hit, BaseHit, SearchResponse } from 'instantsearch.js/es/types'
import { UserResources } from '@leroy-merlin-br/backpack-services'

import { useUserResources } from 'scripts/react-components/shared/hooks'

import { GetInsightsStoredInfoProps, setInsightsInfo } from '../utils'

type AlgoliaContextProps = {
  isMobileFilterOpen: boolean
  toggleMobileFilter: () => void
  query: string
  results: SearchResponse<Hit<BaseHit>> | undefined
  gaTrack?: string | null
  userToken?: string | null
  pageType: string
  type: string
  baseUrl: string
  isSearch: boolean
  sendEventToAlgolia: (type: string, hit: Hit<BaseHit>, event: string) => void
  hits: Hit<BaseHit>[]
  userResources: UserResources | undefined
}

export const AlgoliaContext = createContext<AlgoliaContextProps>(
  {} as AlgoliaContextProps
)

interface AlgoliaContextProviderProps {
  children: ReactNode
  pageType: string
  type: string
  baseUrl: string
  isSearch: boolean
}

const AlgoliaContextProvider = ({
  children,
  pageType,
  type,
  baseUrl,
  isSearch
}: AlgoliaContextProviderProps) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const { results, sendEvent, hits } = useHits()
  const { indexUiState } = useInstantSearch()

  const query = indexUiState?.query || ''

  const meta = (name: string, attribute: string) =>
    document.querySelector(`meta[name="${name}"]`)?.getAttribute(attribute)

  const gaTrack = meta('ga-track', 'content')
  const { userResources } = useUserResources()

  const userToken = userResources?.id

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen)
  }

  const sendEventToAlgolia = (
    type: string,
    hit: Hit<BaseHit>,
    event: string
  ) => {
    const hitObj = {
      queryID: hit.__queryID,
      indexName: results?.index,
      position: hit.__position,
      objectID: hit.objectID
    }

    setInsightsInfo(hitObj as GetInsightsStoredInfoProps)
    sendEvent(type, hit, event)
  }

  const value = {
    isMobileFilterOpen,
    toggleMobileFilter,
    query,
    results,
    gaTrack,
    userToken,
    pageType,
    type,
    baseUrl,
    isSearch,
    hits,
    sendEventToAlgolia,
    userResources
  } as AlgoliaContextProps

  return (
    <AlgoliaContext.Provider value={value}>{children}</AlgoliaContext.Provider>
  )
}

const useAlgoliaContext = () => useContext(AlgoliaContext)

export { AlgoliaContextProvider, useAlgoliaContext }
